import React, { Component, useRef, useState } from 'react';
import { FaQuoteLeft, FaStar } from 'react-icons/fa';
import SectionHeader from '../SectionHeader/SectionHeader';
import axios from 'axios';
import AppUrl from '../../restApi/AppUrl';

/* ── Brand tokens ────────────────────────────────────────────────── */
const C = {
  primary900:   '#0D1630',
  primary700:   '#1B2B5C',
  primary600:   '#2D4E96',
  primary100:   '#D4DCEF',
  accent600:    '#237A4B',
  accent500:    '#2E8B54',
  accent100:    '#E8F8EE',
  secondary200: '#94DDB8',
  neutral600:   '#525252',
  neutral400:   '#a3a3a3',
  neutral200:   '#e5e5e5',
  neutral100:   '#f5f5f5',
  green:        '#2E8B54',
  sectionBg:    '#F9F8F6',
};

/* ── Animation constants ────────────────────────────────────────── */
const CARD_W   = 290; // px — used only for duration calculation
const CARD_GAP = 16;  // px — gap between cards
const SPEED    = 42;  // px/s — scroll speed

/* ─────────────────────────────────────────────────────────────────
 *  Global CSS
 *  Pure-CSS marquee:  transform + @keyframes — GPU-composited, runs
 *  on every mobile browser without JS scroll-position tricks.
 * ───────────────────────────────────────────────────────────────── */
const GLOBAL_CSS = `
  /* hide scrollbar if ever needed */
  .soya-no-scroll::-webkit-scrollbar { display: none; }
  .soya-no-scroll { -ms-overflow-style: none; scrollbar-width: none; }

  /* marquee outer — clips the running track */
  .soya-m-outer {
    overflow: hidden;
    position: relative;
    width: 100%;
    padding: 6px 0 14px;
  }

  /* running track */
  .soya-m-track {
    display: flex;
    width: max-content;
    will-change: transform;
    /* duration injected via CSS variable from the parent's style= */
  }
  .soya-m-left  { animation: soya-go-left  var(--dur, 26s) linear infinite; }
  .soya-m-right { animation: soya-go-right var(--dur, 26s) linear infinite; }

  /* pause class toggled by React state */
  .soya-m-paused { animation-play-state: paused !important; }

  /* seamless loops: left row slides to -50%, right slides back to 0 */
  @keyframes soya-go-left  {
    from { transform: translateX(0);    }
    to   { transform: translateX(-50%); }
  }
  @keyframes soya-go-right {
    from { transform: translateX(-50%); }
    to   { transform: translateX(0);    }
  }

  /* skeleton pulse */
  @keyframes soya-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.45; }
  }

  /* mobile overrides */
  @media (max-width: 767px) {
    .soya-testi-section { padding: 40px 0 48px !important; }
    .soya-testi-header  { margin-bottom: 28px !important; }
    .soya-testi-rows    { gap: 10px !important; }
    .soya-edge-fade     { width: 20px !important; }
  }
`;

/* ── Stars ──────────────────────────────────────────────────────── */
const Stars = ({ n = 5 }) => (
  <div style={{ display: 'flex', gap: 3, marginBottom: 11 }}>
    {[1, 2, 3, 4, 5].map(i => (
      <FaStar
        key={i}
        style={{ fontSize: 13, color: i <= n ? '#F59E0B' : C.neutral200 }}
      />
    ))}
  </div>
);

/* ── Avatar — handles img-error gracefully via state ────────────── */
const Avatar = ({ name = '', img }) => {
  const [broken, setBroken] = useState(false);
  const init = name.trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';
  const ring = { border: `2px solid ${C.secondary200}`, flexShrink: 0 };
  const circle = { width: 44, height: 44, borderRadius: '50%' };

  if (img && !broken) {
    return (
      <img
        src={img} alt={name}
        onError={() => setBroken(true)}
        style={{ ...circle, ...ring, objectFit: 'cover' }}
      />
    );
  }
  return (
    <div style={{
      ...circle, ...ring,
      background: `linear-gradient(135deg, ${C.primary700}, ${C.primary600})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 14, fontWeight: 700, color: '#fff',
    }}>
      {init}
    </div>
  );
};

/* ── Card ───────────────────────────────────────────────────────── */
const Card = ({ item }) => (
  <article style={{
    width: 'clamp(256px, 78vw, 300px)',
    flexShrink: 0,
    background: '#fff',
    borderRadius: 16,
    padding: '20px 18px 16px',
    margin: `0 ${CARD_GAP / 2}px`,
    border: `1px solid ${C.neutral200}`,
    boxShadow: '0 2px 14px rgba(16,42,67,0.07), 0 1px 3px rgba(16,42,67,0.04)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    userSelect: 'none',
    WebkitUserSelect: 'none',
  }}>
    {/* top accent bar */}
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, height: 3,
      background: `linear-gradient(90deg, ${C.green}, ${C.accent500}, ${C.primary700})`,
    }} />

    <FaQuoteLeft style={{ fontSize: 22, color: C.secondary200, marginBottom: 8 }} />
    <Stars n={item.rating} />

    <p style={{
      color: C.neutral600, fontSize: 13.5, lineHeight: 1.75,
      fontFamily: 'Poppins, sans-serif', fontWeight: 400,
      flexGrow: 1, marginBottom: 16,
      display: '-webkit-box', WebkitLineClamp: 6,
      WebkitBoxOrient: 'vertical', overflow: 'hidden',
    }}>
      "{item.review}"
    </p>

    <div style={{ height: 1, background: C.neutral100, marginBottom: 14 }} />

    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Avatar name={item.name} img={item.profile_image} />
      <div>
        <p style={{
          margin: '0 0 2px', fontSize: 13.5, fontWeight: 700,
          color: C.primary900, fontFamily: 'Quicksand, sans-serif',
        }}>
          {item.name}
        </p>
        <p style={{
          margin: 0, fontSize: 11.5, color: C.accent600,
          fontFamily: 'Poppins, sans-serif', fontWeight: 500,
        }}>
          {item.position}{item.organization ? `, ${item.organization}` : ''}
        </p>
      </div>
    </div>
  </article>
);

/* ─────────────────────────────────────────────────────────────────
 *  MarqueeRow
 *
 *  Items are doubled so the seamless loop works: the animation
 *  translates exactly -50% (left) or +0 → -50% reversed (right),
 *  at which point the duplicated second half looks identical to the
 *  start — the browser loops transparently.
 *
 *  Pause on hover (desktop) and on touch (mobile) via CSS class
 *  toggled by React state — no JS scroll manipulation needed.
 * ───────────────────────────────────────────────────────────────── */
const MarqueeRow = ({ items, direction }) => {
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const doubled = [...items, ...items];

  // Keep speed (px/s) constant regardless of how many items there are
  const totalHalfW = items.length * (CARD_W + CARD_GAP);
  const dur = `${Math.max(totalHalfW / SPEED, 8).toFixed(1)}s`;

  const pause = () => {
    clearTimeout(timerRef.current);
    setPaused(true);
  };
  const resume = (delay = 0) => {
    clearTimeout(timerRef.current);
    if (delay > 0) {
      timerRef.current = setTimeout(() => setPaused(false), delay);
    } else {
      setPaused(false);
    }
  };

  const trackCls = [
    'soya-m-track',
    direction === 'left' ? 'soya-m-left' : 'soya-m-right',
    paused ? 'soya-m-paused' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className="soya-m-outer"
      style={{ '--dur': dur }}
      onMouseEnter={pause}
      onMouseLeave={() => resume(0)}
      onTouchStart={pause}
      onTouchEnd={() => resume(2200)}
    >
      {/* left edge fade */}
      <div className="soya-edge-fade" style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 56, zIndex: 2,
        background: `linear-gradient(to right, ${C.sectionBg} 20%, transparent)`,
        pointerEvents: 'none',
      }} />
      {/* right edge fade */}
      <div className="soya-edge-fade" style={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: 56, zIndex: 2,
        background: `linear-gradient(to left, ${C.sectionBg} 20%, transparent)`,
        pointerEvents: 'none',
      }} />

      <div className={trackCls}>
        {doubled.map((item, i) => (
          <Card key={`${item.id}-${i}`} item={item} />
        ))}
      </div>
    </div>
  );
};

/* ── Placeholder data shown while API loads ─────────────────────── */
const PLACEHOLDERS = [
  {
    id: 1, name: 'Fatuma Warsame', position: 'Community Leader', organization: 'Benadir Region',
    review: 'SOYAN transformed how our youth engage with peacebuilding. Their programs gave our young people a voice and a platform to lead real change in our community.',
    rating: 5, profile_image: null,
  },
  {
    id: 2, name: 'Ahmed Hassan', position: 'Program Officer', organization: 'UNHCR Somalia',
    review: 'Partnering with SOYAN has been one of the most impactful collaborations we have had. Their on-the-ground knowledge and dedication to youth empowerment is unmatched.',
    rating: 5, profile_image: null,
  },
  {
    id: 3, name: 'Khadija Ali', position: 'Youth Coordinator', organization: 'Mogadishu Municipality',
    review: 'The vocational training SOYAN provided gave me skills I use every day. I now run my own small business and support my family — this truly changed my life.',
    rating: 5, profile_image: null,
  },
  {
    id: 4, name: 'Mohamed Ibrahim', position: 'Education Supervisor', organization: 'Ministry of Education',
    review: "SOYAN's school dropout prevention initiative has kept hundreds of at-risk youth in education. The data speaks for itself — real results in the hardest-to-reach communities.",
    rating: 5, profile_image: null,
  },
  {
    id: 5, name: 'Amina Yusuf', position: 'Gender Equality Advocate', organization: 'UN Women',
    review: 'Their gender-inclusive approach is exemplary. SOYAN ensures girls are never left behind, and the measurable impact on young women in these communities is truly inspiring.',
    rating: 5, profile_image: null,
  },
  {
    id: 6, name: 'Dr. Omar Farah', position: 'Country Director', organization: 'Save the Children',
    review: 'SOYAN is a model for community-led development. They listen to the people they serve, adapt quickly, and deliver results that no external organisation could replicate alone.',
    rating: 5, profile_image: null,
  },
];

/* ─────────────────────────────────────────────────────────────────
 *  ensure(arr) — pads the array so the marquee has enough cards to
 *  fill the viewport width before the duplicate starts. Aim for at
 *  least 6 items (≈ 1.8 k px) so no gap is ever visible.
 * ───────────────────────────────────────────────────────────────── */
const ensure = (arr, source) => {
  if (!arr.length) return source.slice(0, 3);
  const times = Math.ceil(6 / arr.length);
  return Array.from({ length: times }, () => arr).flat();
};

/* ── Main component ─────────────────────────────────────────────── */
class Testimonials extends Component {
  constructor(props) {
    super(props);
    this.state = { testimonials: [], loading: true };
  }

  componentDidMount() {
    axios.get(AppUrl.Testimonials)
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        this.setState({ testimonials: data, loading: false });
      })
      .catch(() => this.setState({ testimonials: [], loading: false }));
  }

  render() {
    const { testimonials, loading } = this.state;
    const items = testimonials.length >= 1 ? testimonials : PLACEHOLDERS;

    // ≤4 testimonials → single centred row; more → two opposing rows
    const singleRow = items.length <= 4;
    const row1 = ensure(singleRow ? items : items.slice(0, Math.ceil(items.length / 2)), items);
    const row2 = singleRow ? null : ensure(items.slice(Math.ceil(items.length / 2)), items);

    return (
      <section className="soya-testi-section" style={{
        background: `linear-gradient(180deg, ${C.sectionBg} 0%, #ffffff 55%, ${C.sectionBg} 100%)`,
        padding: '72px 0 80px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <style>{GLOBAL_CSS}</style>

        {/* Decorative blobs */}
        <div style={{
          position: 'absolute', top: -120, right: -120,
          width: 360, height: 360, borderRadius: '50%',
          background: `radial-gradient(circle, ${C.accent100} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: -80, left: -80,
          width: 280, height: 280, borderRadius: '50%',
          background: `radial-gradient(circle, ${C.primary100} 0%, transparent 70%)`,
          opacity: 0.5, pointerEvents: 'none',
        }} />

        {/* Section header */}
        <div className="soya-testi-header" style={{ maxWidth: 1280, margin: '0 auto', padding: '0 16px', marginBottom: 48 }}>
          <SectionHeader
            badge="Testimonials"
            title="What Our Partners & Communities Have To Say"
            accentWord="Have To Say"
            subtitle="Real voices from the communities we serve and the partners who walk alongside us."
            variant="light"
            align="center"
          />
        </div>

        {/* Marquee rows */}
        {loading ? (
          <div style={{ display: 'flex', gap: 20, justifyContent: 'center', padding: '0 16px' }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{
                width: 'clamp(220px, 60vw, 300px)', height: 200, borderRadius: 16,
                background: C.neutral100,
                animation: 'soya-pulse 1.5s ease-in-out infinite',
                animationDelay: `${(i - 1) * 0.2}s`,
              }} />
            ))}
          </div>
        ) : (
          <div className="soya-testi-rows" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <MarqueeRow items={row1} direction="left" />
            {!singleRow && <MarqueeRow items={row2} direction="right" />}
          </div>
        )}
      </section>
    );
  }
}

export default Testimonials;
